// @skade/contracts — Heimevernets nasjonale struktur som relasjonelt tre.
//
// Modellerer HV strengt hierarkisk (parent-child) på den kanoniske echelon-aksen
// (hierarchy.ts), med distrikts-metadata (standkvarter, I-styrke, faste
// spesialområder, URS) OG distriktsspesifikke unntak (HV-01 har to URS). Et
// tradisjonelt HV-område ekspanderes til et ORBAT-tre via en fast mal:
//
//   Område (kompani)  ──┬─ Stab-/støttetropp (tropp)
//                        ├─ Geværtropp × N (tropp) ──┬─ Troppsstab (lag)
//                        │                            └─ Geværlag × M (lag)
//                        └─ …
//
// Ren, testbar data + byggere. Kristiansund HV-område (HV-11-203) er fullt
// instansiert som referanse (kommuner + territoriell ansvarsgrense + nøkkelobjekt).
import { z } from 'zod';
import { OrbatUnitSchema } from './orbat.js';
/**
 * Faste enheter direkte under distriktet.
 *
 * - `Stabsområde`: OFFENTLIG bekreftet som fast enhet i alle distrikter — «navet i
 *   den militære organisasjonen», organisert i tropper (forsvaret.no).
 * - `Sensorområde`: OFFENTLIG som et NYTT konsept under utvikling (ISR/
 *   informasjonsinnhenting), der HV-01 er pilotdistriktet (HVSKS + FFI, NTF 2025).
 *   Det er altså ennå ikke en standardenhet i hvert distrikt. Fagansvarlig (HV-01)
 *   oppgir egennavnet «Oslofjord Sensorområde» (OSO) — egennavnet er ikke offentlig
 *   verifisert, men er nettopp derfor tjenstlig, ikke en feil.
 */
const STD_SPECIAL = ['Stabsområde', 'Sensorområde'];
/**
 * ⚠️ TERRITORIELL OMRÅDEINNDELING NEDENFOR ER SYNTETISK STILLAS — IKKE REELL
 * HV-STRUKTUR.
 *
 * `areas`-listene per distrikt er ILLUSTRATIVE kommune-grupperinger for at appen
 * skal ha noe å tegne, ikke de faktiske HV-områdene. Den reelle områdestrukturen
 * er TJENSTLIG. Konkret: fagansvarlig (HV-01-staben) har bekreftet at HV-01 har
 * **19 områder**, at de faktiske navnene ikke skal stå her, og at det seedede
 * settet var feil (bl.a. «Follo», som HV-01 ikke har — Follo/Akershus tilhører
 * HV-02). En tidligere kommentar påsto «kilde: brukerens oversikt»; den påstanden
 * var usann og er fjernet. Distriktet administrerer sine egne reelle områder via
 * `HvAreaSeed`-modellen — det er den eneste autoritative kilden.
 *
 * DISTRIKTSNIVÅET (id, navn, standkvarter, innsatsstyrke, AO på fylkesnivå) ER
 * offentlig og er verifisert mot forsvaret.no + Wikipedia + folkogforsvar 2026-07.
 *
 * `urs` = Utrykningsstyrke (URS = UtRykningsStyrke), oppgitt av fagansvarlig som en
 * fast komponent — én eller flere per distrikt. IKKE å forveksle med
 * «Utrykningseskadronen», som er en egen, distinkt navngitt HV-02-enhet (eskadron,
 * kavaleritradisjon) — det er noe annet enn URS. De konkrete URS-navnene nedenfor
 * er syntetiske stillas på linje med `areas`.
 */
/**
 * True: den territorielle `areas`-inndelingen i HV_DISTRICTS er syntetisk stillas,
 * ikke reell HV-struktur (som er tjenstlig). En konsument som viser områdenavn bør
 * markere dem som illustrative. Distriktsnivået (navn/HQ/innsatsstyrke/AO på
 * fylkesnivå) er derimot offentlig verifisert og kan vises som faktum.
 */
export const OMRADER_ER_SYNTETISK_STILLAS = true;
export const HV_DISTRICTS = [
    {
        // AO rettet: HV-01 dekker Østfold + Vestfold. IKKE Akershus — det er HV-02s AO
        // (verifisert forsvaret.no + Wikipedia 2026-07). Derfor er «Follo» fjernet under:
        // Follo ligger i Akershus og hørte aldri til HV-01. De øvrige gruppene er
        // SYNTETISK STILLAS (se modul-header) — HV-01 har 19 reelle, tjenstlige områder.
        id: 'HV-01', name: 'Oslofjord', hq: 'Rygge', ao: 'Østfold og Vestfold',
        responseForce: 'Polar Bear VI', specialAreas: STD_SPECIAL, urs: ['URS 01'],
        areas: [
            { name: 'Halden', municipalities: ['Halden', 'Aremark'] },
            { name: 'Sarpsborg', municipalities: ['Sarpsborg', 'Rakkestad'] },
            { name: 'Fredrikstad', municipalities: ['Fredrikstad', 'Hvaler'] },
            { name: 'Moss', municipalities: ['Moss', 'Våler', 'Råde'] },
            { name: 'Indre Østfold', municipalities: ['Indre Østfold', 'Marker', 'Skiptvet'] },
            { name: 'Nord-Jarlsberg', municipalities: ['Holmestrand', 'Horten'] },
            { name: 'Tønsberg', municipalities: ['Tønsberg', 'Færder'] },
            { name: 'Sandefjord', municipalities: ['Sandefjord'] },
            { name: 'Larvik', municipalities: ['Larvik'] },
        ],
    },
    {
        id: 'HV-02', name: 'Oslo og Akershus', hq: 'Lutvann', ao: 'Oslo by og store deler av Akershus',
        responseForce: 'Derby', specialAreas: STD_SPECIAL, urs: ['URS 02'],
        areas: [
            { name: 'Oslo Sentrum', municipalities: ['Oslo (sentrumskjernen)'], note: 'Kritisk infrastruktur i sentrum' },
            { name: 'Oslo Nord', municipalities: ['Oslo (nordlige bydeler)'] },
            { name: 'Oslo Øst', municipalities: ['Oslo (Groruddalen/østlige bydeler)'] },
            { name: 'Oslo Sør', municipalities: ['Oslo (Søndre Nordstrand, Nordstrand)'] },
            { name: 'Oslo Vest', municipalities: ['Oslo (Ullern, Vestre Aker, Frogner)'] },
            { name: 'Bærum', municipalities: ['Bærum'] },
            { name: 'Asker', municipalities: ['Asker'] },
            { name: 'Nedre Romerike', municipalities: ['Lillestrøm', 'Lørenskog', 'Rælingen', 'Enebakk'] },
            { name: 'Øvre Romerike', municipalities: ['Ullensaker', 'Eidsvoll', 'Nannestad', 'Nes'], note: 'OSL Gardermoen' },
        ],
    },
    {
        id: 'HV-03', name: 'Telemark og Buskerud', hq: 'Heistadmoen', ao: 'Buskerud og Telemark',
        responseForce: 'Gunnerside', specialAreas: STD_SPECIAL, urs: ['URS 03'],
        areas: [
            { name: 'Drammen', municipalities: ['Drammen', 'Lier'] },
            { name: 'Kongsberg', municipalities: ['Kongsberg', 'Flesberg', 'Rollag'] },
            { name: 'Ringerike', municipalities: ['Ringerike', 'Hole', 'Krødsherad'] },
            { name: 'Hallingdal', municipalities: ['Flå', 'Nesbyen', 'Gol', 'Hemsedal', 'Ål', 'Hol'] },
            { name: 'Skien', municipalities: ['Skien', 'Siljan'] },
            { name: 'Porsgrunn', municipalities: ['Porsgrunn'] },
            { name: 'Bamble', municipalities: ['Bamble', 'Kragerø'] },
            { name: 'Øvre Telemark', municipalities: ['Notodden', 'Tinn', 'Hjartdal', 'Seljord', 'Kviteseid', 'Vinje'] },
            { name: 'Midt-Telemark', municipalities: ['Midt-Telemark', 'Nome'] },
        ],
    },
    {
        id: 'HV-05', name: 'Opplandske', hq: 'Terningmoen', ao: 'Innlandet fylke',
        responseForce: 'Grebe', specialAreas: STD_SPECIAL, urs: ['URS 05'],
        areas: [
            { name: 'Hamar', municipalities: ['Hamar', 'Ringsaker', 'Stange', 'Løten'] },
            { name: 'Elverum', municipalities: ['Elverum', 'Trysil', 'Våler', 'Åsnes'] },
            { name: 'Glåmdal', municipalities: ['Kongsvinger', 'Eidskog', 'Grue', 'Nord-Odal', 'Sør-Odal'] },
            { name: 'Østerdalen', municipalities: ['Åmot', 'Stor-Elvdal', 'Rendalen', 'Tynset', 'Alvdal'] },
            { name: 'Lillehammer', municipalities: ['Lillehammer', 'Øyer', 'Gausdal'] },
            { name: 'Gjøvik og Toten', municipalities: ['Gjøvik', 'Østre Toten', 'Vestre Toten'] },
            { name: 'Hadeland og Land', municipalities: ['Gran', 'Lunner', 'Søndre Land', 'Nordre Land'] },
            { name: 'Valdres', municipalities: ['Nord-Aurdal', 'Sør-Aurdal', 'Vang', 'Øystre Slidre', 'Vestre Slidre'] },
            { name: 'Gudbrandsdal', municipalities: ['Ringebu', 'Sør-Fron', 'Nord-Fron', 'Sel', 'Vågå', 'Lom'] },
        ],
    },
    {
        // Gjenopprettet som eget distrikt av Stortinget juni 2024 (var slått sammen med
        // Rogaland til HV-08 fra 2009). Under oppbygging, full oppbemanning planlagt
        // ~2028 (forsvaret.no «Agder blir nytt HV-distrikt»). Innsatsstyrke Varg er
        // Agder-basert (Kristiansand), men forsvaret.no lister foreløpig både Varg og
        // Osprey under HV-08 — verifiser tildelingen ved neste oppdatering av kilden.
        id: 'HV-07', name: 'Agder', hq: 'Kjevik', ao: 'Agder fylke',
        responseForce: 'Varg', specialAreas: STD_SPECIAL, urs: ['URS 07'],
        areas: [
            { name: 'Kristiansand', municipalities: ['Kristiansand'] },
            { name: 'Østre Agder', municipalities: ['Arendal', 'Grimstad', 'Tvedestrand', 'Risør'] },
            { name: 'Lindesnes', municipalities: ['Lindesnes'] },
            { name: 'Lister', municipalities: ['Farsund', 'Flekkefjord', 'Lyngdal', 'Kvinesdal'] },
            { name: 'Setesdal', municipalities: ['Vennesla', 'Evje og Hornnes', 'Bygland', 'Valle', 'Bykle'] },
        ],
    },
    {
        id: 'HV-08', name: 'Rogaland', hq: 'Vatneleiren', ao: 'Rogaland fylke',
        responseForce: 'Osprey', specialAreas: STD_SPECIAL, urs: ['URS 08'],
        areas: [
            { name: 'Stavanger', municipalities: ['Stavanger'] },
            { name: 'Sandnes', municipalities: ['Sandnes'] },
            { name: 'Sola og Randaberg', municipalities: ['Sola', 'Randaberg'], note: 'Sola flystasjon' },
            { name: 'Jæren', municipalities: ['Klepp', 'Time', 'Hå'] },
            { name: 'Dalane', municipalities: ['Eigersund', 'Sokndal', 'Lund', 'Bjerkreim'] },
            { name: 'Haugalandet', municipalities: ['Haugesund', 'Tysvær', 'Vindafjord', 'Bokn'] },
            { name: 'Karmøy', municipalities: ['Karmøy'], note: 'Kårstø' },
            { name: 'Ryfylke', municipalities: ['Strand', 'Hjelmeland', 'Suldal', 'Sauda'] },
        ],
    },
    {
        id: 'HV-09', name: 'Bergenhus', hq: 'Bergenhus festning', ao: 'Vestland fylke (tidligere Hordaland)',
        responseForce: 'Bjørn West', specialAreas: STD_SPECIAL, urs: ['URS 09'],
        areas: [
            { name: 'Bergenhus', municipalities: ['Bergen (sentrum)'], note: 'Haakonsvern-området' },
            { name: 'Fana', municipalities: ['Bergen (Fana, Ytrebygda)'], note: 'Flesland' },
            { name: 'Åsane', municipalities: ['Bergen (Åsane, Arna)'] },
            { name: 'Sotra og Øygarden', municipalities: ['Øygarden'], note: 'Sture/Kollsnes' },
            { name: 'Nordhordland', municipalities: ['Alver', 'Austrheim', 'Fedje', 'Masfjorden'] },
            { name: 'Voss og Hardanger', municipalities: ['Voss', 'Kvam', 'Ullensvang', 'Eidfjord', 'Ulvik'] },
            { name: 'Sunnhordland', municipalities: ['Stord', 'Bømlo', 'Fitjar', 'Kvinnherad', 'Sveio'] },
            { name: 'Bjørnafjorden', municipalities: ['Bjørnafjorden', 'Samnanger'] },
        ],
    },
    {
        id: 'HV-11', name: 'Møre og Fjordane', hq: 'Setnesmoen', ao: 'Møre og Romsdal, samt nordlige Vestland (tidligere Sogn og Fjordane)',
        responseForce: 'Archery', specialAreas: STD_SPECIAL, urs: ['URS 11'], areaStart: 203,
        areas: [
            { name: 'Ytre Nordmøre', municipalities: ['Kristiansund', 'Averøy', 'Smøla', 'Aure'], note: 'Kristiansund HV-område. Kvernberget lufthavn, Vestbase, Tjeldbergodden.' },
            { name: 'Indre Nordmøre', municipalities: ['Sunndal', 'Surnadal', 'Tingvoll'] },
            { name: 'Ytre Romsdal', municipalities: ['Molde', 'Hustadvika', 'Aukra'] },
            { name: 'Indre Romsdal', municipalities: ['Rauma', 'Vestnes'] },
            { name: 'Ålesund', municipalities: ['Ålesund', 'Sula', 'Giske'] },
            { name: 'Søre Sunnmøre', municipalities: ['Volda', 'Ørsta', 'Herøy', 'Sande', 'Hareid', 'Ulstein'] },
            { name: 'Indre Sunnmøre', municipalities: ['Stranda', 'Sykkylven', 'Fjord'] },
            { name: 'Nordfjord', municipalities: ['Stad', 'Stryn', 'Gloppen', 'Bremanger'] },
            { name: 'Sunnfjord', municipalities: ['Sunnfjord', 'Kinn', 'Askvoll', 'Fjaler'] },
            { name: 'Sogn', municipalities: ['Sogndal', 'Luster', 'Aurland', 'Lærdal', 'Høyanger', 'Vik'] },
        ],
    },
    {
        id: 'HV-12', name: 'Trøndelag', hq: 'Værnes', ao: 'Trøndelag fylke',
        responseForce: 'Rype', specialAreas: STD_SPECIAL, urs: ['URS 12'],
        areas: [
            { name: 'Nidaros', municipalities: ['Trondheim (sentrum)'], note: 'Ørland/Værnes forsyningsakser' },
            { name: 'Strinda', municipalities: ['Trondheim (østlige)', 'Malvik'] },
            { name: 'Heimdal', municipalities: ['Trondheim (sørlige)', 'Melhus'] },
            { name: 'Stjørdal', municipalities: ['Stjørdal', 'Meråker'] },
            { name: 'Innherred', municipalities: ['Levanger', 'Verdal', 'Steinkjer', 'Inderøy'] },
            { name: 'Namdal', municipalities: ['Namsos', 'Grong', 'Overhalla', 'Nærøysund'] },
            { name: 'Fosen', municipalities: ['Ørland', 'Indre Fosen', 'Åfjord'], note: 'Kampflybase Ørland' },
            { name: 'Orkla', municipalities: ['Orkland', 'Heim', 'Rindal'] },
            { name: 'Gauldal', municipalities: ['Midtre Gauldal', 'Oppdal', 'Holtålen', 'Røros'] },
        ],
    },
    {
        id: 'HV-14', name: 'Sør-Hålogaland', hq: 'Drevjamoen', ao: 'Nordland fylke sør for Tysfjord',
        responseForce: 'Heron', specialAreas: STD_SPECIAL, urs: ['URS 14'],
        areas: [
            { name: 'Sør-Helgeland', municipalities: ['Brønnøy', 'Sømna', 'Vega', 'Bindal'] },
            { name: 'Vefsn', municipalities: ['Vefsn', 'Grane', 'Hattfjelldal'] },
            { name: 'Rana', municipalities: ['Rana', 'Hemnes', 'Nesna', 'Lurøy'] },
            { name: 'Salten', municipalities: ['Fauske', 'Sørfold', 'Saltdal', 'Beiarn'] },
            { name: 'Bodø', municipalities: ['Bodø', 'Gildeskål'], note: 'Flystasjon / FOH Reitan' },
            { name: 'Steigen og Hamsund', municipalities: ['Steigen', 'Hamarøy'] },
        ],
    },
    {
        id: 'HV-16', name: 'Nord-Hålogaland', hq: 'Bjerkvik', ao: 'Nordland nord for Tysfjord, samt Troms fylke',
        responseForce: 'Claymore', specialAreas: STD_SPECIAL, urs: ['URS 16'],
        areas: [
            { name: 'Hålogaland', municipalities: ['Narvik', 'Evenes'], note: 'Evenes flystasjon' },
            { name: 'Vesterålen', municipalities: ['Sortland', 'Andøy', 'Bø', 'Hadsel', 'Øksnes'] },
            { name: 'Lofoten', municipalities: ['Vågan', 'Vestvågøy', 'Flakstad', 'Moskenes'] },
            { name: 'Harstad', municipalities: ['Harstad', 'Kvæfjord', 'Tjeldsund'] },
            { name: 'Midt-Troms', municipalities: ['Bardu', 'Målselv', 'Senja', 'Sørreisa'], note: 'Hærens akser' },
            { name: 'Tromsø', municipalities: ['Tromsø', 'Karlsøy', 'Balsfjord'] },
            { name: 'Nord-Troms', municipalities: ['Lyngen', 'Storfjord', 'Kåfjord', 'Skjervøy', 'Nordreisa'] },
        ],
    },
    {
        id: 'HV-17', name: 'Finnmark', hq: 'Porsangmoen', ao: 'Finnmark fylke',
        responseForce: 'Ida og Lyra', specialAreas: STD_SPECIAL, urs: ['URS 17'],
        areas: [
            { name: 'Alta', municipalities: ['Alta', 'Loppa', 'Kvænangen'] },
            { name: 'Hammerfest', municipalities: ['Hammerfest', 'Hasvik', 'Måsøy'] },
            { name: 'Porsanger', municipalities: ['Porsanger', 'Nordkapp', 'Karasjok'], note: 'Banak / GP' },
            { name: 'Øst-Finnmark', municipalities: ['Sør-Varanger', 'Vadsø', 'Vardø', 'Båtsfjord', 'Tana', 'Nesseby'] },
        ],
    },
];
export function districtById(id) {
    return HV_DISTRICTS.find((d) => d.id === id);
}
/** Distriktenes standkvarter/region-senter (skjematisk, lon/lat). Justerbart. */
export const DISTRICT_CENTERS = {
    'HV-01': [10.72, 59.38], 'HV-02': [10.75, 59.94], 'HV-03': [9.62, 59.62],
    'HV-05': [11.30, 60.90], 'HV-07': [8.00, 58.20], 'HV-08': [5.75, 58.90],
    'HV-09': [5.32, 60.39], 'HV-11': [7.50, 62.65], 'HV-12': [10.90, 63.47],
    'HV-14': [13.30, 65.90], 'HV-16': [17.40, 68.50], 'HV-17': [24.97, 70.05],
};
/** Skjematisk senter (representativt tettsted) per tradisjonelt område. IKKE
 * eksakt geometri — brukes til å plassere ALLE AO-er på kartet slik at man ser
 * eget vs. omkringliggende områder. Erstattes av GeoNorge-kommunegrenser senere. */
export const AREA_CENTERS = {
    // HV-01 Oslofjord
    'HV-01-201': [11.39, 59.13], 'HV-01-202': [11.11, 59.28], 'HV-01-203': [10.93, 59.22], 'HV-01-204': [10.66, 59.43], 'HV-01-205': [11.16, 59.58], 'HV-01-206': [10.84, 59.72], 'HV-01-207': [10.47, 59.42], 'HV-01-208': [10.41, 59.27], 'HV-01-209': [10.22, 59.13], 'HV-01-210': [10.03, 59.05],
    // HV-02 Oslo og Akershus
    'HV-02-201': [10.74, 59.91], 'HV-02-202': [10.79, 59.96], 'HV-02-203': [10.88, 59.96], 'HV-02-204': [10.80, 59.85], 'HV-02-205': [10.70, 59.92], 'HV-02-206': [10.52, 59.89], 'HV-02-207': [10.44, 59.83], 'HV-02-208': [11.05, 59.96], 'HV-02-209': [11.17, 60.14],
    // HV-03 Telemark og Buskerud
    'HV-03-201': [10.20, 59.74], 'HV-03-202': [9.65, 59.67], 'HV-03-203': [10.26, 60.17], 'HV-03-204': [8.95, 60.70], 'HV-03-205': [9.61, 59.21], 'HV-03-206': [9.66, 59.14], 'HV-03-207': [9.42, 58.87], 'HV-03-208': [9.26, 59.56], 'HV-03-209': [9.06, 59.41],
    // HV-05 Opplandske
    'HV-05-201': [11.07, 60.79], 'HV-05-202': [11.56, 60.88], 'HV-05-203': [12.00, 60.19], 'HV-05-204': [10.78, 62.28], 'HV-05-205': [10.46, 61.12], 'HV-05-206': [10.69, 60.80], 'HV-05-207': [10.55, 60.37], 'HV-05-208': [9.23, 60.98], 'HV-05-209': [9.54, 61.77],
    // HV-07 Agder
    'HV-07-201': [8.00, 58.15], 'HV-07-202': [8.77, 58.46], 'HV-07-203': [7.46, 58.03], 'HV-07-204': [6.66, 58.30], 'HV-07-205': [7.79, 58.59],
    // HV-08 Rogaland
    'HV-08-201': [5.73, 58.97], 'HV-08-202': [5.74, 58.85], 'HV-08-203': [5.68, 58.88], 'HV-08-204': [5.65, 58.74], 'HV-08-205': [6.00, 58.45], 'HV-08-206': [5.27, 59.41], 'HV-08-207': [5.28, 59.28], 'HV-08-208': [6.05, 59.02],
    // HV-09 Bergenhus
    'HV-09-201': [5.32, 60.39], 'HV-09-202': [5.35, 60.32], 'HV-09-203': [5.33, 60.47], 'HV-09-204': [5.12, 60.36], 'HV-09-205': [5.29, 60.55], 'HV-09-206': [6.42, 60.63], 'HV-09-207': [5.50, 59.78], 'HV-09-208': [5.47, 60.18],
    // HV-11 Møre og Fjordane (start 203)
    'HV-11-203': [7.73, 63.11], 'HV-11-204': [8.57, 62.67], 'HV-11-205': [7.16, 62.74], 'HV-11-206': [7.69, 62.57], 'HV-11-207': [6.15, 62.47], 'HV-11-208': [6.13, 62.20], 'HV-11-209': [6.95, 62.31], 'HV-11-210': [6.00, 61.91], 'HV-11-211': [5.85, 61.45], 'HV-11-212': [7.10, 61.23],
    // HV-12 Trøndelag
    'HV-12-201': [10.40, 63.43], 'HV-12-202': [10.47, 63.41], 'HV-12-203': [10.28, 63.29], 'HV-12-204': [10.92, 63.47], 'HV-12-205': [11.50, 63.97], 'HV-12-206': [11.50, 64.47], 'HV-12-207': [9.70, 63.70], 'HV-12-208': [9.85, 63.30], 'HV-12-209': [9.69, 62.59],
    // HV-14 Sør-Hålogaland
    'HV-14-201': [12.21, 65.47], 'HV-14-202': [13.19, 65.84], 'HV-14-203': [14.14, 66.31], 'HV-14-204': [15.39, 67.26], 'HV-14-205': [14.40, 67.28], 'HV-14-206': [15.02, 67.93],
    // HV-16 Nord-Hålogaland
    'HV-16-201': [17.43, 68.44], 'HV-16-202': [15.41, 68.70], 'HV-16-203': [14.57, 68.23], 'HV-16-204': [16.54, 68.80], 'HV-16-205': [18.51, 69.07], 'HV-16-206': [18.96, 69.65], 'HV-16-207': [21.03, 69.77],
    // HV-17 Finnmark
    'HV-17-201': [23.27, 69.97], 'HV-17-202': [23.68, 70.66], 'HV-17-203': [24.97, 70.07], 'HV-17-204': [29.75, 70.07],
};
/** Tradisjonelle områder for et distrikt med stabile id-er (nummerert fra
 * `areaStart`, default 201; HV-11 fra 203 → Ytre Nordmøre = HV-11-203). */
export function districtAreas(d) {
    const start = d.areaStart ?? 201;
    return d.areas.map((a, i) => {
        const id = `${d.id}-${start + i}`;
        return { ...a, district: d.id, number: start + i, id, center: AREA_CENTERS[id] };
    });
}
/** Alle tradisjonelle områder nasjonalt (flat), med tildelte id-er. */
export function allAreas() {
    return HV_DISTRICTS.flatMap(districtAreas);
}
/** Slå opp ett område på full id («HV-11-203»). */
export function areaById(fullId) {
    return allAreas().find((a) => a.id === fullId);
}
// ── Kommune → HV-område/-distrikt (territoriell dekning) ─────────────────────
/** Normaliser et kommunenavn for matching: små bokstaver, uten parentes-tillegg,
 * uten «tidligere». Tospråklige navn («Deatnu - Tana») håndteres av kommuneKeys. */
export function normKommune(s) {
    return s.toLowerCase().replace(/\(.*?\)/g, '').replace(/tidligere/g, '').trim().replace(/\s+/g, ' ');
}
/** Alle match-nøkler for et kommunenavn: hele navnet + hver del av et tospråklig
 * «Norsk - Samisk»-navn, så både «Tana» og «Deatnu - Tana» treffer. */
export function kommuneKeys(name) {
    const keys = new Set([normKommune(name)]);
    for (const part of name.split(/\s*[-–]\s*/)) {
        const n = normKommune(part);
        if (n.length >= 3)
            keys.add(n);
    }
    return [...keys];
}
/** Fylkesnummer (2 første siffer i kommunenummer) → dominerende HV-distrikt.
 * Fallback for kommuner som ikke er navngitt i noe områdes kommuneliste. Noen
 * fylker deles mellom distrikter (Akershus, Vestland, Nordland) — navnematch tar
 * de fleste riktig; fallbacken gir dominerende distrikt som distrikt/FOH justerer. */
export const FYLKE_NR_TO_DISTRICT = {
    '31': 'HV-01', '39': 'HV-01', // Østfold, Vestfold
    '03': 'HV-02', '32': 'HV-02', // Oslo, Akershus
    '33': 'HV-03', '40': 'HV-03', // Buskerud, Telemark
    '34': 'HV-05', // Innlandet
    '42': 'HV-07', // Agder
    '11': 'HV-08', // Rogaland
    '46': 'HV-09', // Vestland
    '15': 'HV-11', // Møre og Romsdal
    '50': 'HV-12', // Trøndelag
    '18': 'HV-14', // Nordland
    '55': 'HV-16', // Troms
    '56': 'HV-17', // Finnmark
};
/** Distrikt for et kommunenummer via fylkesnummer-prefikset. */
export function districtForKommuneNr(kommunenummer) {
    return FYLKE_NR_TO_DISTRICT[String(kommunenummer).slice(0, 2)];
}
/** Indeks kommunenavn (normalisert, inkl. tospråklige deler) → område-id. */
let _kommuneIndex = null;
export function kommuneAreaIndex() {
    if (_kommuneIndex)
        return _kommuneIndex;
    const idx = new Map();
    for (const a of allAreas()) {
        for (const m of a.municipalities) {
            for (const k of kommuneKeys(m))
                if (!idx.has(k))
                    idx.set(k, a.id);
        }
    }
    _kommuneIndex = idx;
    return idx;
}
/**
 * Beste-anslag kommune → område for kommuner som IKKE er navngitt i en
 * områdeliste (nærmeste område-senter i samme distrikt). Justerbart: distrikt/FOH
 * kan overstyre. Nøkkel = kommunenummer. Markeres «estimated» i UI (stiplet).
 */
export const KOMMUNE_AREA_OVERRIDES = {
    '1122': 'HV-08-208', '1144': 'HV-08-207', '1151': 'HV-08-207',
    '1511': 'HV-11-210', '1557': 'HV-11-203', '1580': 'HV-11-207',
    '1816': 'HV-14-201', '1820': 'HV-14-201', '1822': 'HV-14-202', '1827': 'HV-14-201', '1835': 'HV-14-201', '1836': 'HV-14-202', '1837': 'HV-14-203', '1851': 'HV-14-206', '1856': 'HV-14-202', '1857': 'HV-14-205',
    '3226': 'HV-02-209', '3230': 'HV-02-208', '3232': 'HV-02-203', '3236': 'HV-02-206', '3242': 'HV-02-209',
    '3314': 'HV-03-202', '3316': 'HV-03-201', '3332': 'HV-03-202', '3338': 'HV-03-204',
    '3425': 'HV-05-204', '3426': 'HV-05-204', '3429': 'HV-05-209', '3430': 'HV-05-204', '3431': 'HV-05-209', '3432': 'HV-05-209', '3433': 'HV-05-208', '3450': 'HV-05-208',
    '4016': 'HV-03-209', '4030': 'HV-03-209', '4032': 'HV-03-209', '4034': 'HV-03-209',
    '4211': 'HV-07-202', '4212': 'HV-07-202', '4214': 'HV-07-202', '4215': 'HV-07-201', '4216': 'HV-07-201', '4217': 'HV-07-202', '4218': 'HV-07-205', '4224': 'HV-07-205', '4226': 'HV-07-203', '4228': 'HV-07-204',
    '4611': 'HV-09-207', '4616': 'HV-09-208', '4625': 'HV-09-204', '4627': 'HV-09-204', '4628': 'HV-09-206', '4629': 'HV-09-206', '4630': 'HV-09-203', '4635': 'HV-09-205', '4636': 'HV-09-205', '4637': 'HV-09-205', '4643': 'HV-09-206',
    '5014': 'HV-12-207', '5020': 'HV-12-201', '5022': 'HV-12-209', '5029': 'HV-12-208', '5032': 'HV-12-204', '5033': 'HV-12-204', '5036': 'HV-12-204', '5041': 'HV-12-206', '5042': 'HV-12-206', '5043': 'HV-12-206', '5044': 'HV-12-206', '5046': 'HV-12-206', '5049': 'HV-12-206', '5052': 'HV-12-206', '5056': 'HV-12-207',
    '5514': 'HV-16-201', '5516': 'HV-16-201', '5518': 'HV-16-201', '5522': 'HV-16-201', '5528': 'HV-16-201',
    '5612': 'HV-17-201', '5624': 'HV-17-203', '5626': 'HV-17-204', '5630': 'HV-17-204',
};
/**
 * Hvem har territorielt ansvar for en kommune? Navnematch mot områdenes
 * kommunelister først (gir konkret område + distrikt); ellers fylke-fallback
 * (gir distrikt, område ikke tildelt). Garanterer distrikts-dekning for alle
 * norske kommuner. `kommunenummer` brukes til fylke-fallbacken.
 */
export function kommuneResponsibility(name, kommunenummer) {
    const idx = kommuneAreaIndex();
    for (const k of kommuneKeys(name)) {
        const areaId = idx.get(k);
        if (areaId)
            return { areaId, district: districtOfAreaId(areaId), assigned: true, estimated: false };
    }
    // Beste-anslag (nærmeste område i samme distrikt) — justerbart.
    const override = kommunenummer ? KOMMUNE_AREA_OVERRIDES[String(kommunenummer)] : undefined;
    if (override)
        return { areaId: override, district: districtOfAreaId(override), assigned: true, estimated: true };
    const district = kommunenummer ? districtForKommuneNr(kommunenummer) : undefined;
    return { district, assigned: false, estimated: false };
}
/** «HV-11-203» → «HV-11». */
function districtOfAreaId(areaId) {
    return areaId.slice(0, areaId.lastIndexOf('-'));
}
export const DEFAULT_OMRADE_SHAPE = { rifleTroops: 4, squadsPerTroop: 4 };
/**
 * Ekspander et tradisjonelt HV-område til et ORBAT-tre på echelon-aksen:
 * område (kompani) → stab-/støttetropp + N geværtropper (tropp) → troppsstab +
 * M geværlag (lag). `areaId` brukes som id-prefiks for stabile, unike node-id-er.
 */
export function buildOmradeOrbat(areaId, areaName, shape = DEFAULT_OMRADE_SHAPE) {
    const units = [];
    const mk = (u) => units.push(OrbatUnitSchema.parse(u));
    const rootId = areaId;
    mk({ id: rootId, name: areaName, echelon: 'kompani' });
    // Stab- og støttetropp
    const stabId = `${areaId}.stab`;
    mk({ id: stabId, name: 'Stab- og støttetropp', echelon: 'tropp', parentId: rootId, authorized: 24 });
    // Geværtropper
    const troops = Math.max(1, Math.min(6, shape.rifleTroops));
    const squads = Math.max(1, Math.min(6, shape.squadsPerTroop));
    for (let t = 1; t <= troops; t++) {
        const troopId = `${areaId}.tr${t}`;
        mk({ id: troopId, name: `${t}. geværtropp`, echelon: 'tropp', parentId: rootId });
        // Troppsstab
        mk({ id: `${troopId}.stab`, name: 'Troppsstab', echelon: 'lag', parentId: troopId, authorized: 4 });
        // Geværlag (8–10 pr. lag → 9)
        for (let s = 1; s <= squads; s++) {
            mk({ id: `${troopId}.lag${s}`, name: `${s}. geværlag`, echelon: 'lag', parentId: troopId, authorized: 9 });
        }
    }
    return units;
}
// ── Territoriell ansvarsgrense + nøkkelobjekter ─────────────────────────────
/** Kritisk objekt innenfor et HV-områdes ansvar (objektsikring). */
export const KeyObjectSchema = z.object({
    id: z.string().max(64),
    name: z.string().max(200),
    lon: z.number().min(-180).max(180),
    lat: z.number().min(-90).max(90),
    category: z.enum(['lufthavn', 'forsyningsbase', 'industri', 'havn', 'kraft', 'annet']).default('annet'),
    note: z.string().max(1000).default(''),
});
/** Et HV-områdes territorielle ansvarsområde (AOR). */
export const AreaOfResponsibilitySchema = z.object({
    areaId: z.string().max(64), // «HV-11-203»
    name: z.string().max(200),
    district: z.string().max(16), // «HV-11»
    municipalities: z.array(z.string().max(120)).default([]),
    /** Ytre ansvarsgrense som lukket ring [ [lon,lat], … ]. Skjematisk inntil
     * eksakte kommunegrenser (GeoNorge/Kartverket) lastes inn. */
    boundary: z.array(z.tuple([z.number(), z.number()])).default([]),
    approximate: z.boolean().default(true),
    keyObjects: z.array(KeyObjectSchema).default([]),
});
// ── Kristiansund HV-område (HV-11-203) — fullt instansiert ──────────────────
/** Ytre/kystnære Nordmøre: Kristiansund, Averøy, Smøla, Aure. Skjematisk ring
 * (erstattes med eksakte kommunegrenser når GeoNorge-data lastes). */
export const KRISTIANSUND_AOR = AreaOfResponsibilitySchema.parse({
    areaId: 'HV-11-203',
    name: 'Kristiansund HV-område',
    district: 'HV-11',
    municipalities: ['Kristiansund', 'Averøy', 'Smøla', 'Aure'],
    approximate: true,
    boundary: [
        [7.85, 63.52], // Smøla N
        [8.30, 63.46], // Smøla NØ
        [8.72, 63.40], // Aure NØ
        [8.78, 63.22], // Aure Ø
        [8.55, 63.12], // Aure S (Tjeldbergodden-halvøya)
        [8.05, 63.00], // sør mot Kristiansund
        [7.72, 62.96], // Kristiansund S
        [7.42, 62.97], // Averøy SV
        [7.38, 63.10], // Averøy V
        [7.55, 63.32], // kyst nordover
        [7.85, 63.52], // lukk
    ],
    keyObjects: [
        { id: 'kvernberget', name: 'Kvernberget lufthavn', lon: 7.8245, lat: 63.1118, category: 'lufthavn', note: 'Sivil luftfart, helikopter til sokkelen, potensiell alliert mottak.' },
        { id: 'vestbase', name: 'Vestbase', lon: 7.7205, lat: 63.1163, category: 'forsyningsbase', note: 'Forsyningsbase for olje/gass i Norskehavet.' },
        { id: 'tjeldbergodden', name: 'Tjeldbergodden', lon: 8.5665, lat: 63.2833, category: 'industri', note: 'Prosesserer gass fra Heidrun; nøkkelobjekt for energisikkerhet.' },
    ],
});
/** ORBAT-treet for Kristiansund HV-område (kompani → tropper → lag). */
export function kristiansundOrbat(shape) {
    return buildOmradeOrbat(KRISTIANSUND_AOR.areaId, KRISTIANSUND_AOR.name, shape);
}
//# sourceMappingURL=hv-structure.js.map